// New Ballard Score — gestational age estimation via a 12-criterion assessment
// (6 physical maturity + 6 neuromuscular). Multi-step wizard optimised for
// one-handed bedside use on a phone.
//
// Reference: Ballard JL, Khoury JC, Wedig K, Wang L, Eilers-Walsman BL, Lipp R.
// New Ballard Score, expanded to include extremely premature infants.
// J Pediatr. 1991;119(3):417-423.
//
// Score→week mapping is linear (verified from the reference table): each
// 5-point increment corresponds to +2 weeks, anchored at score -10 → 20 wk.

import React, { useState, useMemo } from "react";
import {
  Baby,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  Copy,
  CheckCircle2,
  Info,
  Mars,
  Venus,
} from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { cn } from "@/lib/utils";
import { NeuromuscularIllustration } from "@/components/BallardIllustrations";

// ---------- criteria data ----------

const NEURO_SCORES = [-1, 0, 1, 2, 3, 4];
const PHYS_SCORES = [-1, 0, 1, 2, 3, 4, 5];

const NEUROMUSCULAR = [
  {
    key: "posture",
    en: "Posture",
    id: "Postur",
    hintEn: "Supine, at rest. Observe degree of flexion of arms and legs.",
    hintId: "Bayi telentang tanpa stimulasi. Nilai derajat fleksi lengan dan tungkai.",
    labels: {
      "-1": { en: "Fully extended arms & legs", id: "Lengan & tungkai lurus penuh" },
      0: { en: "Extended limbs, minimal flexion", id: "Ekstensi, fleksi minimal" },
      1: { en: "Some flexion of legs", id: "Sedikit fleksi tungkai" },
      2: { en: "Stronger leg flexion; arms extended", id: "Fleksi tungkai kuat; lengan lurus" },
      3: { en: "Arms slightly flexed, legs flexed", id: "Lengan sedikit fleksi, tungkai fleksi" },
      4: { en: "Full flexion of arms and legs", id: "Fleksi penuh lengan & tungkai" },
    },
  },
  {
    key: "square_window",
    en: "Square Window (Wrist)",
    id: "Square Window (Pergelangan Tangan)",
    hintEn: "Flex hand toward forearm. Measure angle between palm and forearm.",
    hintId: "Fleksikan tangan ke arah lengan bawah. Ukur sudut antara telapak dan lengan bawah.",
    labels: {
      "-1": { en: "> 90°", id: "> 90°" },
      0: { en: "90°", id: "90°" },
      1: { en: "60°", id: "60°" },
      2: { en: "45°", id: "45°" },
      3: { en: "30°", id: "30°" },
      4: { en: "0° (touches forearm)", id: "0° (menyentuh lengan bawah)" },
    },
  },
  {
    key: "arm_recoil",
    en: "Arm Recoil",
    id: "Arm Recoil",
    hintEn: "Fully flex forearm × 5 s, then fully extend and release. Angle at rebound.",
    hintId: "Fleksikan lengan bawah 5 detik, kemudian ekstensikan penuh dan lepaskan. Nilai sudut rebound.",
    labels: {
      "-1": { en: "180° — no recoil", id: "180° — tidak ada recoil" },
      0: { en: "140–180°", id: "140–180°" },
      1: { en: "110–140°", id: "110–140°" },
      2: { en: "90–110°", id: "90–110°" },
      3: { en: "< 90°", id: "< 90°" },
      4: { en: "< 90° — brisk", id: "< 90° — cepat" },
    },
  },
  {
    key: "popliteal",
    en: "Popliteal Angle",
    id: "Sudut Popliteal",
    hintEn: "Flex hip at 90°, extend lower leg. Measure the angle behind the knee.",
    hintId: "Fleksikan panggul 90°, ekstensikan tungkai bawah. Ukur sudut di belakang lutut.",
    labels: {
      "-1": { en: "180°", id: "180°" },
      0: { en: "160°", id: "160°" },
      1: { en: "140°", id: "140°" },
      2: { en: "120°", id: "120°" },
      3: { en: "100°", id: "100°" },
      4: { en: "90°", id: "90°" },
    },
  },
  {
    key: "scarf",
    en: "Scarf Sign",
    id: "Scarf Sign",
    hintEn: "Draw hand across chest toward opposite shoulder. Where does the elbow land?",
    hintId: "Tarik tangan menyeberang dada ke bahu berlawanan. Di mana siku berhenti?",
    labels: {
      "-1": { en: "Elbow past opposite shoulder", id: "Siku melewati bahu kontralateral" },
      0: { en: "At opposite axillary line", id: "Di garis aksila kontralateral" },
      1: { en: "At contralateral midclavicular", id: "Di midklavikula kontralateral" },
      2: { en: "At midline (sternum)", id: "Di garis tengah (sternum)" },
      3: { en: "Between midline & ipsilateral nipple", id: "Antara midline & puting ipsilateral" },
      4: { en: "Elbow stays at ipsilateral side", id: "Siku tetap di sisi ipsilateral" },
    },
  },
  {
    key: "heel_ear",
    en: "Heel to Ear",
    id: "Tumit ke Telinga",
    hintEn: "Grasp foot and draw toward ear. Note the resistance point.",
    hintId: "Pegang kaki dan tarik menuju telinga. Catat titik hambatan.",
    labels: {
      "-1": { en: "Heel touches ear", id: "Tumit menyentuh telinga" },
      0: { en: "Near ear", id: "Dekat telinga" },
      1: { en: "Eye level", id: "Setinggi mata" },
      2: { en: "Nipple level", id: "Setinggi puting" },
      3: { en: "Umbilicus level", id: "Setinggi umbilikus" },
      4: { en: "Below umbilicus", id: "Di bawah umbilikus" },
    },
  },
];

const PHYSICAL = [
  {
    key: "skin",
    en: "Skin",
    id: "Kulit",
    labels: {
      "-1": { en: "Sticky, friable, transparent", id: "Lengket, rapuh, transparan" },
      0: { en: "Gelatinous, red, translucent", id: "Gelatin, merah, tembus pandang" },
      1: { en: "Smooth pink, visible veins", id: "Merah muda halus, vena tampak" },
      2: { en: "Superficial peeling &/or rash", id: "Pengelupasan superfisial / ruam" },
      3: { en: "Cracking, pale areas, rare veins", id: "Retak, area pucat, vena jarang" },
      4: { en: "Parchment, deep cracking, no vessels", id: "Seperti perkamen, retak dalam" },
      5: { en: "Leathery, cracked, wrinkled", id: "Seperti kulit, retak, keriput" },
    },
  },
  {
    key: "lanugo",
    en: "Lanugo",
    id: "Lanugo",
    labels: {
      "-1": { en: "None", id: "Tidak ada" },
      0: { en: "Sparse", id: "Jarang" },
      1: { en: "Abundant", id: "Banyak" },
      2: { en: "Thinning", id: "Menipis" },
      3: { en: "Bald areas", id: "Area botak" },
      4: { en: "Mostly bald", id: "Sebagian besar botak" },
      5: { en: "Absent", id: "Tidak ada sama sekali" },
    },
  },
  {
    key: "plantar",
    en: "Plantar Surface",
    id: "Permukaan Plantar",
    labels: {
      "-1": { en: "Heel-toe 40–50 mm: −1", id: "Panjang tumit-jari 40–50 mm: −1" },
      0: { en: "> 50 mm, no crease", id: "> 50 mm, tanpa garis" },
      1: { en: "Faint red marks", id: "Garis merah samar" },
      2: { en: "Anterior transverse crease only", id: "Hanya garis transversal anterior" },
      3: { en: "Creases on anterior 2/3", id: "Garis pada 2/3 anterior" },
      4: { en: "Creases over entire sole", id: "Garis di seluruh telapak" },
      5: { en: "Deep creases over entire sole", id: "Garis dalam di seluruh telapak" },
    },
  },
  {
    key: "breast",
    en: "Breast",
    id: "Payudara",
    labels: {
      "-1": { en: "Imperceptible", id: "Tidak tampak" },
      0: { en: "Barely perceptible", id: "Hampir tidak tampak" },
      1: { en: "Flat areola, no bud", id: "Areola datar, tidak ada bud" },
      2: { en: "Stippled areola, 1–2 mm bud", id: "Areola tampak, bud 1–2 mm" },
      3: { en: "Raised areola, 3–4 mm bud", id: "Areola menonjol, bud 3–4 mm" },
      4: { en: "Full areola, 5–10 mm bud", id: "Areola penuh, bud 5–10 mm" },
      5: { en: "Full areola, > 10 mm bud", id: "Areola penuh, bud > 10 mm" },
    },
  },
  {
    key: "eye_ear",
    en: "Eye / Ear",
    id: "Mata / Telinga",
    labels: {
      "-1": { en: "Lids fused loosely (-1)", id: "Kelopak menyatu longgar (-1)" },
      0: { en: "Lids open; pinna flat, stays folded", id: "Kelopak terbuka; pinna datar, tetap terlipat" },
      1: { en: "Slight curve of pinna; slow recoil", id: "Pinna sedikit melengkung; recoil lambat" },
      2: { en: "Well-curved pinna; soft, ready recoil", id: "Pinna melengkung baik; recoil siap" },
      3: { en: "Formed & firm; instant recoil", id: "Terbentuk & keras; recoil segera" },
      4: { en: "Thick cartilage; ear stiff", id: "Tulang rawan tebal; kaku" },
      5: { en: "Full recoil, mature cartilage", id: "Recoil penuh, tulang rawan matur" },
    },
  },
  {
    key: "genitals",
    en: "Genitals",
    id: "Genitalia",
    conditional: true,
    male: {
      labels: {
        "-1": { en: "Scrotum flat, smooth", id: "Skrotum datar, halus" },
        0: { en: "Scrotum empty, faint rugae", id: "Skrotum kosong, rugae samar" },
        1: { en: "Testes in upper canal, rare rugae", id: "Testis di kanalis atas, rugae jarang" },
        2: { en: "Testes descending, few rugae", id: "Testis turun, sedikit rugae" },
        3: { en: "Testes down, good rugae", id: "Testis turun, rugae baik" },
        4: { en: "Testes pendulous, deep rugae", id: "Testis pendulous, rugae dalam" },
        5: { en: "Testes pendulous, deep rugae (mature)", id: "Testis pendulous, rugae dalam (matur)" },
      },
    },
    female: {
      labels: {
        "-1": { en: "Clitoris prominent, labia flat", id: "Klitoris menonjol, labia datar" },
        0: { en: "Clitoris prominent, small labia minora", id: "Klitoris menonjol, labia minora kecil" },
        1: { en: "Clitoris prominent, enlarging labia minora", id: "Klitoris menonjol, labia minora membesar" },
        2: { en: "Labia majora & minora equally prominent", id: "Labia majora & minora sama menonjol" },
        3: { en: "Labia majora large, minora small", id: "Labia majora besar, minora kecil" },
        4: { en: "Labia majora cover clitoris & minora", id: "Labia majora menutupi klitoris & minora" },
        5: { en: "Labia majora cover clitoris & minora (mature)", id: "Labia majora menutupi klitoris & minora (matur)" },
      },
    },
  },
];

// ---------- score → weeks (linear) ----------
function scoreToWeeks(total) {
  // Table anchor: -10 → 20 wk; each 5-pt step is +2 wk.
  // Formula: weeks = 20 + (total + 10) / 2.5
  const raw = 20 + (total + 10) / 2.5;
  // Clamp to table domain 20–44 weeks.
  return Math.max(20, Math.min(44, raw));
}

function weeksClassification(w, isID) {
  if (w < 32) return isID ? "Preterm sangat / ekstrem" : "Very / extremely preterm";
  if (w < 34) return isID ? "Preterm sedang" : "Moderately preterm";
  if (w < 37) return isID ? "Late preterm" : "Late preterm";
  if (w < 39) return isID ? "Early term" : "Early term";
  if (w < 41) return isID ? "Term" : "Term";
  if (w < 42) return isID ? "Late term" : "Late term";
  return isID ? "Postterm" : "Post-term";
}

// ---------- component ----------

export default function Ballard() {
  const { lang } = useLanguage();
  const isID = lang === "id";
  const T = (obj) => (isID ? obj.id : obj.en);

  // Steps: 0 = sex select, 1..6 = neuromuscular, 7..12 = physical, 13 = result.
  const [step, setStep] = useState(0);
  const [sex, setSex] = useState(null); // "male" | "female"
  const [neuroScores, setNeuroScores] = useState({});
  const [physScores, setPhysScores] = useState({});
  const [copied, setCopied] = useState(false);

  const totalSteps = 1 + NEUROMUSCULAR.length + PHYSICAL.length + 1; // 14

  const neuroSum = useMemo(
    () => NEUROMUSCULAR.reduce((a, c) => a + (neuroScores[c.key] ?? 0), 0),
    [neuroScores]
  );
  const physSum = useMemo(
    () => PHYSICAL.reduce((a, c) => a + (physScores[c.key] ?? 0), 0),
    [physScores]
  );
  const filledCount =
    Object.keys(neuroScores).length + Object.keys(physScores).length;
  const total = neuroSum + physSum;
  const weeks = scoreToWeeks(total);
  const allFilled = filledCount === NEUROMUSCULAR.length + PHYSICAL.length;

  const reset = () => {
    setStep(0);
    setSex(null);
    setNeuroScores({});
    setPhysScores({});
    setCopied(false);
  };

  // ---- Copy report ----
  const copyReport = () => {
    const lines = [
      isID ? "New Ballard Score" : "New Ballard Score",
      `Sex: ${sex}`,
      "",
      isID ? "Neuromuskular:" : "Neuromuscular:",
      ...NEUROMUSCULAR.map((c) => `  ${T(c)}: ${neuroScores[c.key]}`),
      isID ? "Fisik:" : "Physical:",
      ...PHYSICAL.map((c) => `  ${T(c)}: ${physScores[c.key]}`),
      "",
      `Total: ${total}`,
      `${isID ? "Usia gestasi" : "Gestational age"}: ${weeks.toFixed(1)} ${isID ? "minggu" : "weeks"}`,
    ];
    navigator.clipboard?.writeText(lines.join("\n")).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  };

  // ---- What are we rendering this step? ----
  const criterionForStep = (s) => {
    if (s === 0) return { kind: "sex" };
    if (s >= 1 && s <= 6) return { kind: "neuro", data: NEUROMUSCULAR[s - 1] };
    if (s >= 7 && s <= 12) return { kind: "phys", data: PHYSICAL[s - 7] };
    return { kind: "result" };
  };

  const cur = criterionForStep(step);

  // ---- Nav guards ----
  const canGoNext = () => {
    if (cur.kind === "sex") return !!sex;
    if (cur.kind === "neuro") return neuroScores[cur.data.key] !== undefined;
    if (cur.kind === "phys") return physScores[cur.data.key] !== undefined;
    return false;
  };

  const goNext = () => {
    if (step < totalSteps - 1) setStep(step + 1);
  };
  const goPrev = () => {
    if (step > 0) setStep(step - 1);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="ballard-page">
      {/* ---- Header ---- */}
      <header className="border-b border-stone-200 pb-5 mb-6">
        <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-teal-700 mb-2 flex items-center gap-2">
          <Baby size={14} strokeWidth={1.5} />
          {isID ? "Neonatologi · Estimasi Usia Gestasi" : "Neonatology · Gestational Age Estimation"}
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-medium text-stone-900 tracking-tight leading-tight">
          {isID ? "New Ballard Score" : "New Ballard Score"}
        </h1>
        <p className="mt-3 text-sm md:text-base text-stone-600 leading-relaxed max-w-2xl">
          {isID
            ? "12 kriteria (6 maturitas neuromuskular + 6 maturitas fisik). Estimasi usia gestasi berdasarkan penilaian fisik langsung — tidak menggantikan penilaian klinis."
            : "12 criteria (6 neuromuscular maturity + 6 physical maturity). Estimates gestational age from direct physical assessment — not a substitute for clinical judgment."}
        </p>
      </header>

      {/* ---- Progress ---- */}
      <div className="mb-6" data-testid="ballard-progress">
        <div className="flex items-center justify-between text-[10px] tracking-[0.16em] uppercase font-mono text-stone-500 mb-2">
          <span>
            {isID ? "Langkah" : "Step"} {step + 1} / {totalSteps}
          </span>
          <span>{isID ? "Terisi" : "Filled"}: {filledCount}/12</span>
        </div>
        <div className="h-1.5 w-full bg-stone-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-sky-700 transition-all duration-200"
            style={{ width: `${((step + 1) / totalSteps) * 100}%` }}
          />
        </div>
      </div>

      {/* ---- Step content ---- */}
      <div className="border border-stone-200 bg-white rounded-md p-5 md:p-8 min-h-[420px]" data-testid="ballard-step-card">
        {cur.kind === "sex" && (
          <SexStep isID={isID} sex={sex} setSex={setSex} />
        )}

        {cur.kind === "neuro" && (
          <CriterionStep
            kind="neuro"
            criterion={cur.data}
            isID={isID}
            value={neuroScores[cur.data.key]}
            setValue={(v) => setNeuroScores({ ...neuroScores, [cur.data.key]: v })}
            scoreDomain={NEURO_SCORES}
          />
        )}

        {cur.kind === "phys" && (
          <CriterionStep
            kind="phys"
            criterion={cur.data}
            isID={isID}
            value={physScores[cur.data.key]}
            setValue={(v) => setPhysScores({ ...physScores, [cur.data.key]: v })}
            scoreDomain={PHYS_SCORES}
            sex={sex}
          />
        )}

        {cur.kind === "result" && (
          <ResultStep
            isID={isID}
            sex={sex}
            neuroScores={neuroScores}
            physScores={physScores}
            neuroSum={neuroSum}
            physSum={physSum}
            total={total}
            weeks={weeks}
            allFilled={allFilled}
            copied={copied}
            copyReport={copyReport}
          />
        )}
      </div>

      {/* ---- Nav controls ---- */}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3" data-testid="ballard-nav">
        <button
          type="button"
          onClick={goPrev}
          disabled={step === 0}
          data-testid="ballard-prev"
          className={cn(
            "inline-flex items-center gap-2 border-2 rounded-md px-4 py-2.5 min-h-[44px] text-sm font-medium transition-colors",
            step === 0
              ? "border-stone-200 text-stone-400 cursor-not-allowed"
              : "border-stone-300 text-stone-700 hover:bg-stone-50"
          )}
        >
          <ChevronLeft size={16} />
          {isID ? "Sebelumnya" : "Previous"}
        </button>

        <button
          type="button"
          onClick={reset}
          data-testid="ballard-reset"
          className="inline-flex items-center gap-2 border-2 border-stone-200 text-stone-500 hover:text-stone-800 hover:border-stone-400 rounded-md px-3 py-2.5 min-h-[44px] text-xs font-medium transition-colors"
        >
          <RotateCcw size={14} />
          {isID ? "Reset" : "Reset"}
        </button>

        <button
          type="button"
          onClick={goNext}
          disabled={!canGoNext() || step === totalSteps - 1}
          data-testid="ballard-next"
          className={cn(
            "inline-flex items-center gap-2 border-2 rounded-md px-4 py-2.5 min-h-[44px] text-sm font-medium transition-colors",
            !canGoNext() || step === totalSteps - 1
              ? "border-stone-200 text-stone-400 cursor-not-allowed"
              : "border-sky-700 bg-sky-700 text-white hover:bg-sky-800"
          )}
        >
          {step === totalSteps - 2 ? (isID ? "Lihat Hasil" : "Show Result") : isID ? "Berikutnya" : "Next"}
          <ChevronRight size={16} />
        </button>
      </div>

      {/* ---- Reference ---- */}
      <div className="mt-8 max-w-2xl">
        <div className="border-l-2 border-amber-700/60 pl-4 py-1 text-xs text-stone-600 leading-relaxed">
          <span className="font-semibold text-stone-900">
            {isID ? "Sebagai rujukan saja." : "Reference only."}
          </span>{" "}
          {isID
            ? "New Ballard Score — Ballard JL et al., J Pediatr 1991;119:417. Estimasi ± 2 minggu; tidak menggantikan penilaian klinis dan riwayat menstruasi ibu."
            : "New Ballard Score — Ballard JL et al., J Pediatr 1991;119:417. Accuracy ± 2 weeks; does not replace clinical judgement or maternal menstrual history."}
        </div>
      </div>
    </div>
  );
}

// ---------- sub components ----------

function SexStep({ isID, sex, setSex }) {
  return (
    <div data-testid="ballard-step-sex">
      <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-stone-500 mb-2">
        {isID ? "Langkah 1 · Pilih jenis kelamin" : "Step 1 · Select sex"}
      </div>
      <h2 className="font-display text-2xl md:text-3xl font-medium text-stone-900 leading-tight">
        {isID ? "Jenis kelamin bayi?" : "Infant's sex?"}
      </h2>
      <p className="mt-3 text-sm text-stone-600 leading-relaxed max-w-lg">
        {isID
          ? "Kriteria genitalia menampilkan pilihan berbeda tergantung jenis kelamin."
          : "The genitals criterion displays sex-specific options."}
      </p>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3 max-w-md">
        {[
          { key: "male", enLabel: "Male", idLabel: "Laki-laki", Icon: Mars, color: "text-sky-700" },
          { key: "female", enLabel: "Female", idLabel: "Perempuan", Icon: Venus, color: "text-rose-700" },
        ].map((opt) => {
          const active = sex === opt.key;
          const Icon = opt.Icon;
          return (
            <button
              key={opt.key}
              type="button"
              data-testid={`ballard-sex-${opt.key}`}
              aria-pressed={active}
              onClick={() => setSex(opt.key)}
              className={cn(
                "flex flex-col items-center justify-center gap-2 border-2 rounded-md p-6 min-h-[130px] transition-all",
                active
                  ? "border-sky-700 bg-sky-700/5 ring-2 ring-sky-700/40"
                  : "border-stone-200 hover:border-stone-400 bg-white"
              )}
            >
              <Icon size={32} strokeWidth={1.5} className={opt.color} />
              <span className="font-display text-lg font-medium text-stone-900">
                {isID ? opt.idLabel : opt.enLabel}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CriterionStep({ kind, criterion, isID, value, setValue, scoreDomain, sex }) {
  // For "genitals", pick sex-specific labels; otherwise use criterion.labels.
  const labels =
    criterion.conditional && sex
      ? criterion[sex].labels
      : criterion.labels;
  const showIllustration = kind === "neuro";
  const title = isID ? criterion.id : criterion.en;
  const hint = isID ? criterion.hintId : criterion.hintEn;

  return (
    <div data-testid={`ballard-step-${kind}-${criterion.key}`}>
      <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-stone-500 mb-2">
        {kind === "neuro"
          ? isID
            ? "Maturitas Neuromuskular"
            : "Neuromuscular Maturity"
          : isID
          ? "Maturitas Fisik"
          : "Physical Maturity"}
      </div>
      <h2 className="font-display text-2xl md:text-3xl font-medium text-stone-900 leading-tight">
        {title}
      </h2>
      {hint && (
        <p className="mt-2 text-sm text-stone-600 leading-relaxed max-w-2xl flex items-start gap-2">
          <Info size={14} className="mt-0.5 text-stone-400 shrink-0" />
          <span>{hint}</span>
        </p>
      )}

      <div
        className={cn(
          "mt-5 grid gap-3",
          showIllustration
            ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-6"
            : "grid-cols-1 md:grid-cols-2"
        )}
        data-testid={`ballard-options-${criterion.key}`}
      >
        {scoreDomain.map((score) => {
          const label = labels[String(score)];
          if (!label) return null; // phys criteria may skip
          const active = value === score;
          return (
            <button
              key={score}
              type="button"
              onClick={() => setValue(score)}
              data-testid={`ballard-option-${criterion.key}-${score}`}
              aria-pressed={active}
              className={cn(
                "text-left border-2 rounded-md p-3 bg-white transition-all min-h-[100px] flex flex-col",
                active
                  ? "border-sky-700 bg-sky-700/5 ring-2 ring-sky-700/40"
                  : "border-stone-200 hover:border-stone-400"
              )}
            >
              {showIllustration && (
                <div className="aspect-square w-full max-w-[110px] mx-auto mb-2 bg-stone-50 rounded-sm border border-stone-100">
                  <NeuromuscularIllustration criterion={criterion.key} score={score} />
                </div>
              )}
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    "inline-flex items-center justify-center h-6 w-6 rounded-full text-[11px] font-mono font-bold border",
                    active
                      ? "bg-sky-700 text-white border-sky-800"
                      : "bg-stone-100 text-stone-700 border-stone-300"
                  )}
                >
                  {score}
                </span>
                <span className="text-xs md:text-[13px] text-stone-800 leading-tight">
                  {isID ? label.id : label.en}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ResultStep({
  isID,
  sex,
  neuroScores,
  physScores,
  neuroSum,
  physSum,
  total,
  weeks,
  allFilled,
  copied,
  copyReport,
}) {
  const classification = weeksClassification(weeks, isID);
  return (
    <div data-testid="ballard-result">
      <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-teal-700 mb-2">
        {isID ? "Hasil" : "Result"}
      </div>
      <h2 className="font-display text-2xl md:text-3xl font-medium text-stone-900 leading-tight">
        {isID ? "Estimasi Usia Gestasi" : "Estimated Gestational Age"}
      </h2>

      {!allFilled && (
        <div className="mt-4 border border-amber-300 bg-amber-50 text-amber-900 rounded-md px-4 py-3 text-sm flex items-start gap-2" data-testid="ballard-incomplete">
          <Info size={16} className="mt-0.5 shrink-0" />
          {isID
            ? "Beberapa kriteria belum diisi — hasil tidak akan akurat. Gunakan tombol Sebelumnya untuk melengkapi."
            : "Some criteria are not filled — the result will be inaccurate. Use Previous to complete them."}
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border-2 border-stone-200 rounded-md p-5">
          <div className="text-[10px] tracking-[0.16em] uppercase font-mono text-stone-500 mb-1">
            {isID ? "Total skor" : "Total score"}
          </div>
          <div className="font-display text-4xl font-semibold text-stone-900" data-testid="ballard-total">
            {total}
          </div>
          <div className="mt-2 text-xs text-stone-500">
            {isID ? "Neuromuskular" : "Neuromuscular"} <span className="font-mono">{neuroSum}</span> · {isID ? "Fisik" : "Physical"}{" "}
            <span className="font-mono">{physSum}</span>
          </div>
        </div>
        <div className="border-2 border-sky-700 bg-sky-700/5 rounded-md p-5">
          <div className="text-[10px] tracking-[0.16em] uppercase font-mono text-sky-700 mb-1">
            {isID ? "Usia gestasi" : "Gestational age"}
          </div>
          <div className="font-display text-4xl font-semibold text-sky-800" data-testid="ballard-weeks">
            {weeks.toFixed(1)}
            <span className="text-xl font-mono text-sky-700 ml-1">{isID ? "mgg" : "wk"}</span>
          </div>
          <div className="mt-2 text-xs text-sky-800">{classification}</div>
        </div>
        <div className="border-2 border-stone-200 rounded-md p-5 flex flex-col justify-between">
          <div>
            <div className="text-[10px] tracking-[0.16em] uppercase font-mono text-stone-500 mb-1">
              {isID ? "Jenis kelamin" : "Sex"}
            </div>
            <div className="font-display text-xl text-stone-900 capitalize">{sex || "—"}</div>
          </div>
          <button
            type="button"
            onClick={copyReport}
            data-testid="ballard-copy"
            className={cn(
              "mt-3 inline-flex items-center justify-center gap-2 border-2 rounded-md px-3 py-2 text-xs font-medium transition-colors",
              copied
                ? "border-teal-600 bg-teal-600 text-white"
                : "border-sky-700 text-sky-700 hover:bg-sky-700 hover:text-white"
            )}
          >
            {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
            {copied ? (isID ? "Tersalin!" : "Copied!") : isID ? "Salin ke rekam medis" : "Copy to record"}
          </button>
        </div>
      </div>

      {/* ---- Breakdown table ---- */}
      <div className="mt-6 border border-stone-200 rounded-md overflow-hidden" data-testid="ballard-breakdown">
        <div className="grid grid-cols-2 md:grid-cols-4 bg-stone-50 border-b border-stone-200 text-[10px] tracking-[0.16em] uppercase font-mono text-stone-500 px-4 py-2">
          <span>{isID ? "Kriteria" : "Criterion"}</span>
          <span>{isID ? "Kategori" : "Category"}</span>
          <span className="text-right md:text-left">{isID ? "Skor" : "Score"}</span>
          <span className="hidden md:block">{isID ? "Kelas" : "Class"}</span>
        </div>
        {NEUROMUSCULAR.map((c) => (
          <BreakdownRow key={c.key} name={isID ? c.id : c.en} category={isID ? "Neuromuskular" : "Neuromuscular"} score={neuroScores[c.key]} />
        ))}
        {PHYSICAL.map((c) => (
          <BreakdownRow key={c.key} name={isID ? c.id : c.en} category={isID ? "Fisik" : "Physical"} score={physScores[c.key]} />
        ))}
      </div>
    </div>
  );
}

function BreakdownRow({ name, category, score }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 border-b border-stone-100 last:border-b-0 px-4 py-2 text-sm">
      <span className="text-stone-800">{name}</span>
      <span className="text-stone-500 text-xs md:text-sm">{category}</span>
      <span className="font-mono text-right md:text-left text-stone-900">
        {score === undefined ? "—" : score}
      </span>
      <span className="hidden md:block text-stone-400 text-xs" />
    </div>
  );
}
